import { colorFields } from "./_shared";
/**
 * Scroll reveal — a tall scroll-driven band. A small portrait photo sits
 * centred in the viewport; as the page scrolls, the photo grows until it fills
 * the screen edge to edge and its corners square off. A headline sits over it
 * the whole time and flips colour exactly where the photo is behind it.
 *
 * Ported from the EasyOrders `scroll-trans-v1-section` found on the `dark`
 * family (2026-07-20).
 *
 * A NEW type rather than a variant. The checks made first:
 *
 *  - `banner` / `banner-billboard` and `hero` both put a headline over one
 *    photo, but both are static blocks with a fixed height. The entire content
 *    of this section is the TRANSITION — a `banner` with the same image and
 *    words is a different section, not a variant of this one.
 *  - `before-after` is scroll/drag driven but compares TWO images; here there
 *    is one, and the second state is the viewport itself.
 *
 * Their geometry is inline on the section and is the renderer's source:
 * `height: 150vh`, sticky stage `110vh`, initial box `240x360` (`180x270`
 * below their mobile breakpoint), radius `16px` at rest easing to `0`, and a
 * `rgba(0,0,0,.05) → rgba(0,0,0,.45)` scrim over the photo. MEASURED 1350px
 * tall at 390/768/900/992/1200/1440 against a 900px viewport — i.e. the height
 * is viewport-locked (150vh), not fluid, and does not vary with width.
 *
 * The colour flip is their `.stv1-duotone`: `background-clip: text` with two
 * stacked solid gradients, the top one sized to the photo box, so one text
 * layer reads as two colours without duplicating the markup. Ported as-is.
 *
 * Accessibility departure from the source, deliberate: their section animates
 * regardless of `prefers-reduced-motion`. Ours pins the photo to its final
 * full-bleed state and drops the tall scroll stage entirely under that query,
 * so the section still says what it says without moving.
 */
export const scrollRevealSchema = {
    type: "scroll-reveal",
    name: { ar: "صورة بتكبر مع النزول", en: "Scroll reveal" },
    description: {
        ar: "صورة صغيرة بتكبر لحد ما تملى الشاشة وانت بتنزل، والعنوان فوقها.",
        en: "A photo that grows to fill the screen as you scroll, with a headline over it.",
    },
    maxPerTemplate: 1,
    availableOnTemplates: ["home"],
    settings: [
        {
            id: "image",
            type: "image",
            label: { ar: "الصورة", en: "Photo" },
            helpText: {
                ar: "صورة طولية؛ بتبدأ صغيرة وبتكبر لحد ما تملى الشاشة.",
                en: "A portrait photo; it starts small and grows to fill the screen.",
            },
            focalPoint: true,
        },
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "العنوان", en: "Headline" },
            validate: { max: 60 },
        },
        {
            id: "subtitle",
            type: "text",
            i18n: true,
            label: { ar: "سطر تحت العنوان", en: "Subtitle" },
            validate: { max: 80 },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        {
            /**
             * How big the photo is before it starts growing. Their two authored
             * stops (240x360 desktop, 180x270 mobile) are the `medium` row; the
             * outer two are INTERPOLATED on the same 2:3 portrait ratio, since
             * their engine exposes only the one pair.
             */
            id: "startSize",
            type: "select",
            label: { ar: "حجم البداية", en: "Starting size" },
            helpText: {
                ar: "حجم الصورة قبل ما تبدأ تكبر.",
                en: "How large the photo is before it begins to grow.",
            },
            options: [
                { value: "small", label: { ar: "صغير", en: "Small" } },
                { value: "medium", label: { ar: "متوسط", en: "Medium" } },
                { value: "large", label: { ar: "كبير", en: "Large" } },
            ],
            default: "medium",
        },
        {
            id: "overlayOpacity",
            type: "range",
            label: { ar: "تعتيم الصورة", en: "Photo dimming" },
            helpText: {
                ar: "بيغمّق الصورة عشان العنوان يبان.",
                en: "Darkens the photo so the headline stays readable.",
            },
            min: 0,
            max: 80,
            step: 5,
            default: 45,
        },
        {
            id: "textInside",
            type: "color",
            label: { ar: "لون العنوان فوق الصورة", en: "Headline colour over the photo" },
            helpText: {
                ar: "سيبه فاضي عشان ياخد الأبيض.",
                en: "Leave empty for white.",
            },
        },
        {
            id: "textOutside",
            type: "color",
            label: { ar: "لون العنوان بره الصورة", en: "Headline colour off the photo" },
            helpText: {
                ar: "سيبه فاضي عشان ياخد لون نص القسم.",
                en: "Leave empty to inherit the section text colour.",
            },
        },
        ...colorFields(),
    ],
};
//# sourceMappingURL=scroll-reveal.js.map